
import React, { useState, useEffect } from "react";
import {
  Box, Container, Flex, Text, Tabs, TabList, TabPanels,
  Tab, TabPanel, Button, Center, useDisclosure, useToast,
} from "@chakra-ui/react";
import { FiGrid, FiList, FiExternalLink } from "react-icons/fi";
import apiClient from "../../Configuration/apiClient";
import API_CONFIG from "../../Configuration/apiConfig";
import { CoolerImageModal } from "./modals/CoolerImageModal";

// Hooks
import {useFilters}          from "./hooks/useFilters";
import { useStores }       from "./hooks/useStores";
import { useImages }       from "./hooks/useImages";
import { useStoreDetails } from "./hooks/useStoreDetails";
import { useRecalculation } from "./hooks/useRecalculation";
import { useAdjustedQty } from "./hooks/useAdjustedQty";

// Components
import { FilterBar }         from "./components/FilterBar";
import { ScoreCards }        from "./components/ScoreCards";
import { StoreList }         from "./components/StoreList";
import { AbstractView }      from "./components/AbstractView";

// Modals
import { DetailModal }           from "./modals/DetailModal";
import { AdjustedQtyModal }      from "./modals/AdjustedQtyModal";
import { ActivationImageModal }  from "./modals/ActivationImageModal";


function SoftDrinkDp({ fromDate, toDate }) {
  const userRole = localStorage.getItem("userRole") || sessionStorage.getItem("userRole") || "";
  const isOrgUser = userRole === "ORGIUSER";
  const isCocaColaUser = userRole === "CocaColaUSER";
  const isFactsUser = userRole === "FACTSUSER";
  const showOnlyStoreView = isCocaColaUser || isFactsUser;

  const toast = useToast();
  const [activeTab, setActiveTab] = useState(0);

  const [isValidatingAvl, setIsValidatingAvl] = useState(false);
  const [isValidatingAct, setIsValidatingAct] = useState(false);

  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  const filters = useFilters(fromDate, toDate);

  const stores = useStores(fromDate, toDate, filters.getFilterBody);

  const images = useImages();

  const details = useStoreDetails(fromDate, toDate, images);

  
  // Reset everything when dates change
  useEffect(() => {
    images.clearCache(); 
    setActiveTab(0);
    details.resetDetails();
  }, [fromDate, toDate]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.ResizeObserver === "undefined") return;
    const Original = window.ResizeObserver;
    window.ResizeObserver = class extends Original {
      constructor(cb) {
        super((entries, obs) => window.requestAnimationFrame(() => cb(entries, obs)));
      }
    };
    return () => { window.ResizeObserver = Original; };
  }, []);

  const { handleAdjustedQty, adjustedQtyData, isLoadingAdjustedQty, adjustedQtyError, isAdjustedQtyOpen, onAdjustedQtyClose } = useAdjustedQty({
    getStoreId:     () => details.selectedStore?.storeId || details.scoreBreakdown?.store?.storeId,
    getAuditedDate: () => details.selectedStoreDetails?.auditedDate,
  });

  const { handleRecalculation, isRecalculating } = useRecalculation({
  getRunId:  () => details.scoreBreakdown?.store?.runId,
  getStoreId: () => details.scoreBreakdown?.store?.storeId,
  onSuccess: async () => {
    await details.handleStoreClick(
      details.selectedStore,
      images.modalImageIndex,
      true
    );
  },
});


  const handleValidate = async (auditType, setIsLoading) => {
    const runId = details.scoreBreakdown?.availability?.products?.[0]?.runId;
    const storeId = details.selectedStore?.storeId || details.scoreBreakdown?.availability?.products?.[0]?.storeId;
    const auditDate = details.selectedStoreDetails?.auditedDate;
    const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");

    if (!runId || !storeId || !auditDate || !userId) {
      toast({ title: "Missing Data", description: "Required information is missing.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setIsLoading(true);
    try {
      await apiClient.post(`${API_CONFIG.BASE_URL}/api/audit/validate-store`, {
        runId: Number(runId), storeId: Number(storeId), auditType, auditDate, userId: Number(userId),
      });
      toast({ title: "Success", description: "Store validated successfully", status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: "Validation failed", description: err.response?.data?.message || "Failed to validate store", status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  // Edit save
  const handleEditSave = async () => {
    if (details.selectedStore) {
      await details.handleStoreClick(details.selectedStore, images.modalImageIndex,
      true

      );
    }
  };

  
  return (
    <Box>
      <Container maxW="container.2xl">
        {/* Filters */}
        <Box mt="4">
          <FilterBar
            {...filters}
            fromDate={fromDate}
            toDate={toDate}
            totalOutlets={stores.totalOutlets}
            loadingScores={stores.loadingScores}
          />
          <ScoreCards
            redScore={stores.redScore}
            availabilityScore={stores.availabilityScore}
            activationScore={stores.activationScore}
            coolerScore={stores.coolerScore}
            bonusScore={stores.bonusScore}
            loadingScores={stores.loadingScores}
          />
        </Box>

        {/* Error */}
        {(stores.error || details.error || filters.error) && (
          <Box mt={3} p={3} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.200">
            <Text color="red.600" fontSize="sm">
              {stores.error || details.error || filters.error}
            </Text>
          </Box>
        )}

        <Flex mt="8" gap={6} flexDir={{ base: "column", lg: "row" }}>
          {/* Store List */}
          <StoreList
            displayedStores={stores.displayedStores}
            stores={stores.stores}
            searchQuery={stores.searchQuery}
            handleSearch={stores.handleSearch}
            clearSearch={stores.clearSearch}
            currentPage={stores.currentPage}
            setCurrentPage={stores.setCurrentPage}
            pageSize={stores.pageSize}
            loadingStores={stores.loadingStores}
            selectedStoreDetails={details.selectedStoreDetails}
            onStoreClick={details.handleStoreClick}
            scoreTypeFilter={stores.scoreTypeFilter}
            setScoreTypeFilter={stores.setScoreTypeFilter}
          />

          {/* Detail Panel with Tabs */}
          <Box
            flex="1" bg="white" borderRadius="xl" overflow="hidden"
            boxShadow="sm" border="1px solid" borderColor="gray.200" height="600px"
          >
            <Tabs index={activeTab} onChange={setActiveTab} height="100%">
              <TabList
                bg="red.100" p={2} borderRadius="lg" gap={2} mx={4} mt={4}
                display="flex" justifyContent="space-between"
              >
                {[
                  { icon: <FiGrid size={18} />, label: "Store View" },
                  ...(showOnlyStoreView ? [] : [{ icon: <FiList size={18} />, label: "Detail View" }]),
                ].map(({ icon, label }) => (
                  <Tab
                    key={label}
                    flex="1" fontWeight="semibold" fontSize="md" py={3}
                    borderRadius="lg" color="gray.600" gap={2} justifyContent="center"
                    _selected={{ bg: "red.600", color: "white", boxShadow: "md" }}
                    _hover={{ bg: "red.100", color: "red.600" }}
                  >
                    {icon} {label}
                  </Tab>
                ))}
              </TabList>

              <TabPanels height="calc(100% - 73px)" overflowY="auto">
                {/* Abstract View */}
                <TabPanel height="100%" p={4}>
                  <AbstractView
                    loadingStoreDetails={details.loadingStoreDetails}
                    selectedStoreDetails={details.selectedStoreDetails}
                    selectedStoreImages={images.selectedStoreImages}
                    currentImageIndex={images.currentImageIndex}
                    setCurrentImageIndex={images.setCurrentImageIndex}
                    imageRotation={images.imageRotation}
                    setImageRotation={images.setImageRotation}
                    onDownload={images.handleDownloadImage}
                  />
                </TabPanel>

                {/* Detail View — opens modal */}
                {!showOnlyStoreView && (
                  <TabPanel height="100%" width="100%" p={4}>
                    <Box textAlign="center" mt={10}>
                      <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={4}>
                        Detail View
                      </Text>
                      <Text color="gray.600" mb={6}>
                        Click the button below to open detailed view in a popup
                      </Text>
                      <Button
                        colorScheme="red"
                        onClick={onDetailOpen}
                        size="lg"
                        leftIcon={<FiExternalLink />}
                      >
                        Open Detail View
                      </Button>
                    </Box>
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </Box>
        </Flex>
      </Container>

      {/* Modals */}
      <DetailModal
        isOpen={isDetailOpen}
        onClose={onDetailClose}
        isOrgUser={isOrgUser}
        storeData={{
          scoreBreakdown:       details.scoreBreakdown,
          productSummary:       details.productSummary,
          selectedStoreDetails: details.selectedStoreDetails,
          shelfInfo:            details.shelfInfo,
        }}
        images={{
          actualImageUrl:       images.actualImageUrl,
          annotatedImageUrl:    images.annotatedImageUrl,
          modalActualImages:    images.modalActualImages,
          modalAnnotatedImages: images.modalAnnotatedImages,
          modalImageIndex:      images.modalImageIndex,
          onPrevImage:          images.handlePrevModalImage,
          onNextImage:          images.handleNextModalImage,
          loadingCoolerImage:   images.loadingCoolerImage,
          generatePresignedUrls: images.generatePresignedUrls,
          onViewActivation:     (detail) => images.handleViewActivation(detail, toDate),
          onViewCoolerImage:    (fileName) => images.handleViewCoolerImage(fileName, toDate),
        }}
        actions={{
          onRecalculate:  handleRecalculation,
          isRecalculating,
          onValidateAvl:  () => handleValidate("AVL", setIsValidatingAvl),
          isValidatingAvl,
          onValidateAct:  () => handleValidate("ACT", setIsValidatingAct),
          isValidatingAct,
          onAdjustedQty:  handleAdjustedQty,
          onRefresh:      () => { if (details.selectedStore) { details.handleStoreClick(details.selectedStore, images.modalImageIndex, true); } },
        }}
        editModal={{ isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose, onSave: handleEditSave }}
      />

      <AdjustedQtyModal
        isOpen={isAdjustedQtyOpen}
        onClose={onAdjustedQtyClose}
        isLoading={isLoadingAdjustedQty}
        error={adjustedQtyError}
        data={adjustedQtyData}
      />

      <ActivationImageModal
        isOpen={images.isActivationImageOpen}
        onClose={() => images.setIsActivationImageOpen(false)}
        imageUrl={images.activationImageUrl}
        isLoading={images.loadingActivationImage}
        onDownload={images.handleDownloadImage}
        onUpdateActivation={async (detail, form) =>
        await apiClient.put(`/activation/${detail.id}`, form)
      }
      />
      <CoolerImageModal
        isOpen={images.isCoolerImageOpen}
        onClose={() => images.setIsCoolerImageOpen(false)}
        imageUrl={images.coolerImageUrl}
        isLoading={images.loadingCoolerImage}
        onDownload={images.handleDownloadImage}
      />
    </Box>
  );
}

export default SoftDrinkDp;
